import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../Core/store';
import { axiosPrivate } from '../Core/axios';
import { Folder } from '../Models/Folder';
import { folderStatus, chatStatus } from '../Helpers/types';
import { CreateRoadmapParams } from '../Models/Requests/CreateRoadmapParams';
import { RequestExpandFolder } from '../Models/Requests/RequestExpandFolder';
import { RequestSaveFolderName } from '../Models/Requests/RequestSaveFolderName';
import { CreateFolderParams } from '../Models/Requests/CreateFolderParams';
import { Member } from '../Models/Requests/Member';
import { Channel } from '../Models/Channel';
import { Message } from '../Models/Message';

export interface ChatState {
  channels: Array<Channel>;
  messages: Array<Message>;
  members: Array<Member>;
  findMembers: Array<Member>;
  allChatMembers: Array<Member>;
  status: chatStatus;
  errorMessage: string;
  membersOnline: Array<{ fullName: string, _id: string }>;
  typing: boolean;
  typingNames: Array<string>;
}

const initialState: ChatState = {
  channels: [],
  messages: [],
  members: [],
  findMembers: [],
  allChatMembers: [],
  status: "unset",
  errorMessage: "",
  membersOnline: [],
  typing: false,
  typingNames: []
};

export const loadChannel = createAsyncThunk(
  'load-channel',
  async (_id: string) => {    
    return await axiosPrivate.post('api/chat/load',
      JSON.stringify({
        _id: _id
      }),
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    ).then(
      (res) => {
        return res.data;
      }
    );
});


export const saveChannels = createAsyncThunk(
  'save-channels',
  async (channels: Array<Channel>) => {    
    return await axiosPrivate.post('api/chat/save',
      JSON.stringify({ channels: channels }),
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    ).then(
      (res) => {
        return res.data;
      }
    );
});

export const setChannels = createAsyncThunk(
  'set-channels',
  async (channels: Array<Channel>) => {    
      return channels;
});

export const setMessages = createAsyncThunk(
  'set-messages',
  async (messages: Array<Message>) => {    
      return messages;
});

export const setAllChatMembers = createAsyncThunk(
  'set-all-chat-members',
  async (allChatMembers: Array<Member>) => {    
      return allChatMembers;
});

export const setFindMembers = createAsyncThunk(
  'set-find-members',
  async (members: Array<Member>) => {    
      return members;
});

export const setMembersOnline = createAsyncThunk(
  'set-members-online',
  async (membersOnline: Array<{ fullName: string, _id: string}>) => {    
      return membersOnline;
});

export const setChatStatus = createAsyncThunk(
  'set-channel-status',
  async (status: chatStatus) => {    
      return status;
});

export const setTyping = createAsyncThunk(
  'set-channel-typing',
  async (typing: { typing: boolean, typingNames: Array<string>}) => {    
      return typing;
});

export const ChatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder
      .addCase(setChatStatus.fulfilled, (state, action) => {
        state.status = action.payload;
      })
      .addCase(setTyping.fulfilled, (state, action) => {
        state.typing = action.payload.typing;
        state.typingNames = action.payload.typingNames;
      })
      .addCase(setMessages.fulfilled, (state, action) => {
        state.status = 'idle';
        state.messages = [...action.payload];
      })
      .addCase(setChannels.fulfilled, (state, action) => {
        state.status = 'idle';
        state.channels = [...action.payload];
      })
      .addCase(setAllChatMembers.fulfilled, (state, action) => {
        state.status = 'idle';
        state.allChatMembers = [...action.payload];
      })
      .addCase(setFindMembers.fulfilled, (state, action) => {
        state.status = 'idle';
        state.findMembers = [...action.payload];
      })
      .addCase(setMembersOnline.fulfilled, (state, action) => {
        state.status = 'idle';
        state.membersOnline = [...action.payload];
      })
      .addCase(loadChannel.fulfilled, (state, action) => {
        state.status = 'channelLoaded';
        let channels = [...state.channels];

        let index = state.channels.findIndex(c => c.loaded === true);
        if (index !== -1) {
          channels[index].loaded = false;
          channels[index].lastUnloaded = action.payload.lastLoaded;
        }

        let exists = state.channels.findIndex(c => c._id === action.payload._id);
        if (exists !== -1) {
          channels[exists].lastLoaded = action.payload.lastLoaded;
          channels[exists].loaded = true;
        }
        state.channels = channels;
      })
  },
});

export const getChat = (state: RootState) => state.chat;
export default ChatSlice.reducer;
